import { AxiosProgressEvent } from "axios";
import { API_URLs } from "../../constants/api_urls"
import { apiConfigs } from "../../constants/apiConfigs";
import { CollaborationData, CollaborationsData, MilestoneData } from "../../types/collaborations/CollaborationTypes"
import axiosClient from "../../utils/axiosClient"
import { analyticsLog } from "../../lib/analytics";
import { getErrorMessage } from "../../utils/helpers";

export const fetchCollaborations = async (page: number = 1): Promise<CollaborationsData> => {
    const response = await axiosClient.get(apiConfigs.FETCH_COLLABORATIONS.url, { params: { page } });
    return response.data as CollaborationsData
}

export const generateShortURL = async (campaignId: string): Promise<string> => {
    const response = await axiosClient.post(API_URLs.COLLABORATION_SHORT_URL.replace('<campaignId>', campaignId));
    return response.data.collaboration.landing_page_short_url;
}

export const fetchCollaboration = async (campaignId: string): Promise<CollaborationData> => {
    const response = await axiosClient.get(apiConfigs.FETCH_COLLABORATION.url.replace('<campaignId>', campaignId));
    return response.data as CollaborationData;
}

export const uploadCollaborationMedia = async (campaignId: string, posts: string[]) => {
    const response = await axiosClient.put(API_URLs.COLLABORATION.replace('<campaignId>', campaignId), { posts });
    return response.data.post_ids;
}

export const fetchCollaborationMilestone = async (campaignId: string, milestoneId: string): Promise<MilestoneData> => {
    const response = await axiosClient.get(apiConfigs.FETCH_COLLABORATION_MILESTONE.url.replace('<campaignId>', campaignId).replace('<milestoneId>', milestoneId));
    return response.data as MilestoneData;
}

export const fetchCollaborationPermalinkFile = async (
    campaignId: string,
    permalink: string,
    action: string | null = null,
    onDownloadProgress?: ((progressEvent: AxiosProgressEvent) => void)
) => {
    const isDownload = action === 'download';

    const response = await axiosClient.post(
        apiConfigs.FETCH_COLLABORATION_PERMALINK_FILE.url.replace('<campaignId>', campaignId),
        { permalink, ...(isDownload ? { action } : {}) },
        isDownload ? { responseType: 'blob', onDownloadProgress } : undefined
    );

    return isDownload ? response.data : response.data;
};

export const submitMilestoneStep = async (campaignId: string, payload: Record<string, any>) => {

    const formData = new FormData();

    for (let [key, value] of Object.entries(payload)) {
        const formValue = Array.isArray(value) ? value.join(',') : value;
        formData.append(key, formValue);
    }
    try {
        const response = await axiosClient.post(apiConfigs.SUBMIT_COLLABORATION_MILESTONE_STEP.url.replace('<campaignId>', campaignId), formData, {
            headers: {
                'Content-Type': 'multipart/form-data',
            },
        });
        analyticsLog('collab_step_success', {step: payload.step, campaign_id: campaignId})
        return response.data;
    } catch (error) {
        analyticsLog('collab_step_failed', {step: payload.step, campaign_id: campaignId, error: getErrorMessage(error)});
        throw error;
    }
    


}

export const acknowledgeMilestone = async (campaignId: string, milestoneId: string | number) => {
    const response = await axiosClient.post(apiConfigs.ACKNOWLEDGE_MILESTONE.url.replace('<campaignId>', campaignId)
        .replace('<milestoneId>', String(milestoneId))
    );

    return response.data;
}

export const submitStepDeadline = async (campaignId: string, stepSlug: string | number, date: string) => {
    const response = await axiosClient.post(apiConfigs.SUBMIT_STEP_DEADLINE.url.replace('<campaignId>', campaignId)
        .replace('<stepSlug>', String(stepSlug)),
        { date }
    );

    return response.data;
}

export const getSignedGCSUploadURL = async (campaignId: string, payload: { extension: string, content_type: string }): Promise<{ upload_url: string, uuid: string }> => {
    const response = await axiosClient.post(apiConfigs.GET_SIGNED_GCS_URL.url.replace('<campaignId>', campaignId), payload);

    return response.data;
}

export const uploadFile = async (file: File, signedUrl: string) => {
    await fetch(signedUrl, {
        method: "PUT",
        body: file
    })
};

export const uploadLargeFileInChunks = async (
    file: File,
    uploadURL: string,
    onProgress?: (progress: { uploadedBytes: number; totalBytes: number; percentage: number }) => void,
    chunkSize: number = 5 * 1024 * 1024, // 5MB default
): Promise<void> => {
    const totalSize = file.size;
    let offset = 0;

    while (offset < totalSize) {
        const chunk = file.slice(offset, offset + chunkSize);
        const chunkSizeActual = chunk.size;
        const startByte = offset;
        const endByte = offset + chunkSizeActual - 1;

        const headers: HeadersInit = {
            'Content-Type': file.type || 'application/octet-stream',
            'Content-Range': `bytes ${startByte}-${endByte}/${totalSize}`,
        };

        try {
            const response = await fetch(uploadURL, {
                method: 'PUT',
                headers,
                body: chunk,
            });

            // If fetch succeeded and status is OK
            if ([200, 201, 308].includes(response.status)) {
                offset += chunkSizeActual;

                if (onProgress) {
                    onProgress({
                        uploadedBytes: offset,
                        totalBytes: totalSize,
                        percentage: Math.min(100, (offset / totalSize) * 100),
                    });
                }

                if (response.status === 200 || response.status === 201) {
                    // Treat as final chunk successfully uploaded
                    break;
                }

            } else {
                const text = await response.text();
                throw new Error(`Upload failed with status ${response.status}: ${text}`);
            }

        } catch (err: any) {
            // Detect likely CORS error case:
            const isFinalChunk = endByte + 1 === totalSize;
            const isCORSLike = err instanceof TypeError && (err.message.includes("Failed to fetch") || err.message.includes("Load failed"));

            if (isFinalChunk && isCORSLike) {
                console.warn('CORS blocked final chunk response, but assuming upload was successful.');
                // Treat as success and continue
                if (onProgress) {
                    onProgress({
                        uploadedBytes: totalSize,
                        totalBytes: totalSize,
                        percentage: 100,
                    });
                }
                break;
            }

            console.error('Upload failed at chunk', { startByte, endByte, error: err });
            throw err;
        }
    }
};

export const linkGCSFileWithMilestoneStep = async (campaignId: string, payload: { uuid: string, extension: string, content_type: string, step: string }) => {
    const response = await axiosClient.post(apiConfigs.LINK_UPLOADED_GCS_FILE_WITH_MILESTONE_STEP.url.replace('<campaignId>', campaignId), payload);

    return response.data;
}

export const fetchCollaborationTasks = async (filters: Record<string, any> = {}) => {
    const response = await axiosClient.get(apiConfigs.FETCH_COLLABORATIONS_TASKS.url, {params: {...filters}});
    return response.data;
}

export const fetchCollaborationTasksSummary = async () => {
    const response = await axiosClient.get(apiConfigs.FETCH_COLLABORATION_TASKS_SUMMARY.url);
    return response.data;
}

export const fetchURLMetadata = async (url: string) => {
    const response = await axiosClient.post(apiConfigs.FETCH_URL_METADATA.url, {url});
    return response.data;
}